import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import {
  isPushSupported, getPushPermission, subscribeToPush, unsubscribeFromPush, isPushSubscribed,
  getPushSupportState,
} from '../lib/push';

const LABELS = {
  pt: {
    confirmation: 'Confirmação de marcação',
    reminders: 'Lembretes (24h e 2h antes)',
    return: 'Lembrete de retorno',
    changes: 'Alterações e cancelamentos',
    new_booking: 'Nova marcação recebida',
    reschedule: 'Reagendamentos',
    cancellation: 'Cancelamentos',
    payment: 'Pagamento online recebido',
    daily_summary: 'Resumo do dia (após as 22h)',
  },
  en: {
    confirmation: 'Booking confirmation',
    reminders: 'Reminders (24h and 2h before)',
    return: 'Return reminder',
    changes: 'Changes and cancellations',
    new_booking: 'New booking received',
    reschedule: 'Reschedules',
    cancellation: 'Cancellations',
    payment: 'Online payment received',
    daily_summary: 'Daily summary (after 10pm)',
  },
};

export const NotificationPreferences = () => {
  const { language } = useLanguage();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const lang = language === 'pt' ? 'pt' : 'en';

  const refreshPush = useCallback(async () => {
    setPushOn(await isPushSubscribed());
  }, []);

  useEffect(() => {
    api.get('/notifications/preferences')
      .then((r) => setPrefs(r.data?.preferences || {}))
      .catch(() => setPrefs({}));
    refreshPush();
  }, [refreshPush]);

  const savePrefs = async (next) => {
    const prev = prefs;
    setPrefs(next);
    setSaving(true);
    try {
      await api.put('/notifications/preferences', { preferences: next });
    } catch {
      setPrefs(prev);
      toast.error(language === 'pt' ? 'Erro ao guardar preferências.' : 'Failed to save preferences.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => savePrefs({ ...prefs, [key]: !prefs[key] });

  const handlePushToggle = async () => {
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.success(language === 'pt' ? 'Notificações no telemóvel desativadas.' : 'Phone notifications disabled.');
      } else {
        await subscribeToPush();
        setPushOn(true);
        toast.success(language === 'pt' ? 'Notificações no telemóvel ativadas!' : 'Phone notifications enabled!');
      }
    } catch (e) {
      if (e.message === 'unsupported') {
        toast.error(language === 'pt' ? 'O seu navegador não suporta notificações push.' : 'Your browser does not support push.');
      } else if (e.message === 'not-granted' || e.message === 'denied') {
        toast.error(language === 'pt' ? 'Permissão não concedida no navegador.' : 'Permission not granted.');
      } else {
        toast.error(language === 'pt' ? 'Não foi possível alterar as notificações push.' : 'Could not change push notifications.');
      }
    } finally {
      setPushBusy(false);
    }
  };

  if (!prefs) return null;
  const keys = Object.keys(prefs);
  const pushDenied = getPushPermission() === 'denied';
  const pushState = getPushSupportState();

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm space-y-5 animate-fade-in-up" data-testid="notification-preferences">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-[#BFA05F]" />
        <h2 className="font-heading text-lg font-medium text-[#1A1815]">
          {language === 'pt' ? 'Preferências de Notificações' : 'Notification Preferences'}
        </h2>
      </div>

      <div className="space-y-1">
        {keys.map((key) => (
          <div key={key} className="flex items-center justify-between py-2.5 border-b border-[#F1EEE8] last:border-0">
            <span className="text-sm text-[#2C2924]">{LABELS[lang][key] || key}</span>
            <Switch
              checked={!!prefs[key]}
              onCheckedChange={() => toggle(key)}
              disabled={saving}
              data-testid={`pref-toggle-${key}`}
            />
          </div>
        ))}
      </div>

      {isPushSupported() ? (
        <div className="flex items-center justify-between pt-4 border-t border-[#E6E2DA]">
          <div className="flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-[#BFA05F] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#1A1815]">
                {language === 'pt' ? 'Notificações no telemóvel' : 'Phone notifications'}
              </p>
              <p className="text-xs text-[#A8A29A]">
                {pushDenied
                  ? (language === 'pt' ? 'Bloqueado no navegador. Ative nas definições do site.' : 'Blocked in browser settings.')
                  : (language === 'pt' ? 'Receba avisos mesmo com o site fechado.' : 'Get alerts even when the site is closed.')}
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={handlePushToggle}
            disabled={pushBusy || pushDenied}
            variant={pushOn ? 'outline' : 'default'}
            className={`rounded-full h-9 px-4 text-sm ${pushOn ? 'border-[#E6E2DA] text-[#635E54]' : 'bg-[#1A1815] text-white hover:bg-[#BFA05F]'}`}
            data-testid="push-toggle-btn"
          >
            {pushBusy
              ? '...'
              : pushOn
                ? (language === 'pt' ? 'Desativar' : 'Disable')
                : (language === 'pt' ? 'Ativar' : 'Enable')}
          </Button>
        </div>
      ) : (
        <div className="flex items-start gap-3 pt-4 border-t border-[#E6E2DA]" data-testid="push-unavailable">
          <Smartphone className="w-5 h-5 text-[#BFA05F] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-[#1A1815]">
              {language === 'pt' ? 'Notificações no telemóvel' : 'Phone notifications'}
            </p>
            {pushState === 'ios-needs-install' ? (
              <p className="text-xs text-[#A8A29A] mt-0.5 leading-relaxed">
                {language === 'pt'
                  ? 'No iPhone/iPad, para receber avisos toque em Partilhar (⬆️) e "Adicionar ao ecrã principal". Depois abra a app a partir do ícone e ative aqui as notificações.'
                  : 'On iPhone/iPad, tap Share (⬆️) then "Add to Home Screen". Open the app from that icon and enable notifications here.'}
              </p>
            ) : (
              <p className="text-xs text-[#A8A29A] mt-0.5 leading-relaxed">
                {language === 'pt'
                  ? 'Este navegador não suporta notificações no telemóvel. Experimente o Chrome (Android) ou adicione o site ao ecrã principal (iPhone).'
                  : 'This browser does not support phone notifications. Try Chrome (Android) or add the site to your Home Screen (iPhone).'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPreferences;
